import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Campaign, CampaignMode } from '@/api/client'
import { useTargets } from '@/lib/queries'
import { Button, Dialog, ErrorState, Field, FieldGroup, Input, Select, Textarea, useToast } from '@/ui'
import { AffiliateFields, BLANK_AFFILIATE, affiliateError, affiliatePayload, type AffiliateDraft } from './AffiliateFields'
import { MODES, selectableModes, targetsFor, useCampaignMutations } from './shared'

/** "새 캠페인": every field the old form had. Mount it only while open. */
export function NewCampaignDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const { create } = useCampaignMutations()
  const targets = useTargets().data ?? []
  const [name, setName] = useState('')
  const [mode, setMode] = useState<CampaignMode>('trend')
  const [postsPerDay, setPostsPerDay] = useState(1)
  const [platformsCsv, setPlatformsCsv] = useState('threads,reddit')
  const [persona, setPersona] = useState('힙한 톤의 1인 운영자. 진짜 경험담처럼 들리는 글을 쓴다.')
  const [accounts, setAccounts] = useState<Record<string, number>>({})
  const [topicPool, setTopicPool] = useState('')
  const [expand, setExpand] = useState(false)
  const [angles, setAngles] = useState(3)
  const [aff, setAff] = useState<AffiliateDraft>(BLANK_AFFILIATE)
  const [problem, setProblem] = useState<string | null>(null)
  const coupang = mode === 'affiliate'

  const platforms = platformsCsv
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)

  const submit = async () => {
    if (coupang) {
      // One Threads account, as the server requires.
      const found = affiliateError(aff, accounts.threads ?? 0)
      setProblem(found)
      if (found) return
      try {
        const r = await create.mutateAsync({
          Name: name.trim(),
          Mode: 'affiliate',
          Status: 'active',
          PostsPerDay: postsPerDay,
          Timezone: 'Asia/Seoul',
          PlatformConfigs: { threads: { enabled: true, target_id: accounts.threads } },
          Affiliate: affiliatePayload(aff),
        })
        toast.success(`${name.trim()} 캠페인을 만들었다`)
        onClose()
        if (r?.id) navigate(`/campaigns/${r.id}`)
      } catch {
        /* shown in the dialog from create.error */
      }
      return
    }
    const configs: Campaign['PlatformConfigs'] = Object.fromEntries(
      platforms.map((p) => [p, { enabled: true, ...(accounts[p] ? { target_id: accounts[p] } : {}) }]),
    )
    try {
      const r = await create.mutateAsync({
        Name: name.trim(),
        Mode: mode,
        Status: 'active',
        PostsPerDay: postsPerDay,
        Persona: persona,
        Timezone: 'Asia/Seoul',
        JudgeThreshold: 4.0,
        JudgeMaxRetries: 2,
        PlatformConfigs: configs,
        TopicPool:
          mode === 'topic'
            ? topicPool
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean)
            : undefined,
        ExpandTopics: mode === 'topic' ? expand : false,
        TopicAnglesCount: mode === 'topic' ? angles : 3,
      })
      toast.success(`${name.trim()} 캠페인을 만들었다`)
      onClose()
      if (r?.id) navigate(`/campaigns/${r.id}`)
    } catch {
      /* shown in the dialog from create.error */
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title="새 캠페인"
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" loading={create.isPending} disabled={!name.trim()} onClick={() => void submit()}>
            만들기
          </Button>
        </>
      }
    >
      <div className="max-h-[65vh] space-y-4 overflow-y-auto pb-2 pr-1">
        <FieldGroup columns={2}>
          <Field label="이름" required className="sm:col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="모드" hint={MODES.find((m) => m.value === mode)?.note}>
            <Select value={mode} onChange={(e) => setMode(e.target.value as CampaignMode)}>
              {selectableModes(undefined, true).map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="하루 글 수">
            <Input type="number" min={1} value={postsPerDay} onChange={(e) => setPostsPerDay(Number(e.target.value))} />
          </Field>
          {coupang ? (
            <Field label="Threads 계정" required hint="쿠팡 캠페인은 계정 하나에 올린다." className="sm:col-span-2">
              <Select
                value={String(accounts.threads ?? 0)}
                onChange={(e) => {
                  setProblem(null)
                  setAccounts((prev) => ({ ...prev, threads: Number(e.target.value) }))
                }}
              >
                <option value="0">— 고른다 —</option>
                {targetsFor('threads', targets).map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field label="플랫폼 (쉼표로 구분)" className="sm:col-span-2">
              <Input value={platformsCsv} onChange={(e) => setPlatformsCsv(e.target.value)} placeholder="threads,reddit,x" />
            </Field>
          )}
        </FieldGroup>

        {coupang && (
          <AffiliateFields
            value={aff}
            onChange={(next) => {
              setProblem(null)
              setAff(next)
            }}
          />
        )}

        {!coupang && platforms.length > 0 && (
          <FieldGroup title="플랫폼별 계정" columns={2}>
            {platforms.map((p) => (
              <Field key={p} label={`${p} 계정`}>
                <Select
                  value={String(accounts[p] ?? 0)}
                  onChange={(e) => setAccounts((prev) => ({ ...prev, [p]: Number(e.target.value) }))}
                >
                  <option value="0">— 지정 안 함 —</option>
                  {targetsFor(p, targets).map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ))}
          </FieldGroup>
        )}

        {!coupang && (
          <Field label="페르소나">
            <Textarea rows={3} value={persona} onChange={(e) => setPersona(e.target.value)} />
          </Field>
        )}

        {!coupang && mode === 'topic' && (
          <FieldGroup title="주제 풀">
            <Field label="주제 (한 줄에 하나)">
              <Textarea
                rows={5}
                value={topicPool}
                onChange={(e) => setTopicPool(e.target.value)}
                className="font-mono text-meta"
                placeholder={'AI 코딩 도구로 1인 개발 생산성 높이는 법\n개발자가 흔히 놓치는 SQL 인덱싱 함정'}
              />
            </Field>
            <label className="flex items-center gap-2 text-body">
              <input type="checkbox" checked={expand} onChange={(e) => setExpand(e.target.checked)} />
              주제를 LLM으로 여러 각도로 넓힌 뒤 생성
            </label>
            {expand && (
              <Field label="각도 수 (2~8)">
                <Input type="number" min={2} max={8} value={angles} onChange={(e) => setAngles(Number(e.target.value))} />
              </Field>
            )}
          </FieldGroup>
        )}

        {problem != null && (
          <p role="alert" className="text-table text-status-failed">
            {problem}
          </p>
        )}
        {create.error != null && <ErrorState title="만들지 못했다" error={create.error} />}
      </div>
    </Dialog>
  )
}
